import { z } from 'zod';
import {
  ALL_MODULES_VISIBLE,
  VISIBILITY_MODULES,
  type ModuleVisibility,
  type PermissionAction,
  type Role,
  type VisibilityModule,
} from './enums';

/**
 * Permission subjects used by `can(subject, action)` on both the API (ctx.can) and the web (useCan).
 * - the six visibility modules (R-3.3)
 * - `workspace`: principal profile, legal instruments, workspace settings
 * - `members`: invites, roles, module visibility
 * - `comments`: comments and @mentions on any visible record
 * - `consents`: consent records (Principal may create their own)
 * - `billing`: subscription management (owner only)
 */
export const PERMISSION_SUBJECTS = [
  ...VISIBILITY_MODULES,
  'workspace',
  'members',
  'comments',
  'consents',
  'billing',
] as const;
export const PermissionSubject = z.enum(PERMISSION_SUBJECTS);
export type PermissionSubject = z.infer<typeof PermissionSubject>;

const isVisibilityModule = (s: string): s is VisibilityModule =>
  (VISIBILITY_MODULES as readonly string[]).includes(s);

/** Effective module visibility for a role (R-3.4: owner/attorney/principal see everything). */
export function effectiveVisibility(
  role: Role,
  moduleVisibility: Partial<ModuleVisibility> | null | undefined,
): ModuleVisibility {
  if (role === 'owner' || role === 'attorney' || role === 'principal') return { ...ALL_MODULES_VISIBLE };
  const v = moduleVisibility ?? {};
  return {
    gifts: v.gifts === true,
    money: v.money === true,
    care: v.care === true,
    health: v.health === true,
    attorney: v.attorney === true,
    documents: v.documents === true,
  };
}

/** Extra membership state that narrows the matrix. */
export interface PermissionOptions {
  /**
   * R-3.2: an attorney whose registered LPA / deputyship order has not been uploaded yet (membership
   * `lpa_pending`). They can view everything but only add health diary entries, documents and comments —
   * no other edits, invites or packs — until the document naming them is linked on the Principal profile.
   */
  lpaPending?: boolean;
}

/**
 * The permission matrix (spec §3.2, ARCHITECTURE §3). Pure function so the web and API agree.
 * Read-only subscription / archive mode are NOT handled here — the API context layers them on top.
 */
export function can(
  role: Role,
  moduleVisibility: Partial<ModuleVisibility> | null | undefined,
  subject: PermissionSubject,
  action: PermissionAction,
  opts: PermissionOptions = {},
): boolean {
  const vis = effectiveVisibility(role, moduleVisibility);
  const visible = isVisibilityModule(subject) ? vis[subject] : true;

  if (subject === 'billing') return role === 'owner';

  if (role === 'attorney' && opts.lpaPending && action !== 'view') {
    // Restricted attorney (R-3.2): same edit rights as a family member who can see everything; no packs.
    return action === 'edit' && (subject === 'health' || subject === 'documents' || subject === 'comments');
  }

  if (action === 'view') {
    if (subject === 'members') return true;
    return visible;
  }

  if (action === 'export') {
    if (!isVisibilityModule(subject)) return role === 'owner' || role === 'attorney' || role === 'principal';
    if (role === 'owner' || role === 'attorney' || role === 'principal') return true;
    if (role === 'professional') return visible;
    return false;
  }

  // action === 'edit'
  switch (role) {
    case 'owner':
      return true;
    case 'attorney':
      return true; // all except billing (handled above)
    case 'family':
      return visible && (subject === 'health' || subject === 'documents' || subject === 'comments');
    case 'principal':
      return subject === 'consents' || subject === 'comments';
    case 'professional':
      return subject === 'comments';
    default:
      return false;
  }
}

export type Permissions = Record<PermissionSubject, Record<PermissionAction, boolean>>;

/** Full permission map for a membership — sent to the client in the workspace detail. */
export function permissionMap(
  role: Role,
  moduleVisibility: Partial<ModuleVisibility> | null | undefined,
  opts: PermissionOptions = {},
): Permissions {
  const out = {} as Permissions;
  for (const s of PERMISSION_SUBJECTS) {
    out[s] = {
      view: can(role, moduleVisibility, s, 'view', opts),
      edit: can(role, moduleVisibility, s, 'edit', opts),
      export: can(role, moduleVisibility, s, 'export', opts),
    };
  }
  return out;
}
